/**
 * Ô "Dịp chụp" (QĐ 0480, CEO chốt 6a · 7a): hàng nút chọn dịp, ngày/tháng (năm
 * không bắt buộc), "sinh nhật của ai", ghi chú ngắn. Dùng ở form tạo đơn (nằm
 * thẳng trong form) và trong hộp OccasionDialog (năm chỗ còn lại).
 */
import { DayMonthInput } from "@/components/DayMonthInput";
import { Input } from "@/ui/input";
import { cn } from "@/lib/utils";
import { OCCASION_KINDS } from "./api";
import type { OccasionDraft } from "./model";

export function OccasionFields({
  value,
  onChange,
  disabled,
}: {
  value: OccasionDraft;
  onChange: (next: OccasionDraft) => void;
  disabled?: boolean | undefined;
}) {
  const set = (patch: Partial<OccasionDraft>) => onChange({ ...value, ...patch });
  const isBirthday = value.kind === "birthday";
  return (
    <div className="space-y-3">
      <div role="group" aria-label="Chọn dịp chụp" className="flex flex-wrap gap-1.5">
        {OCCASION_KINDS.map((k) => {
          const on = value.kind === k.kind;
          return (
            <button
              key={k.kind}
              type="button"
              aria-pressed={on}
              disabled={disabled}
              onClick={() => set({ kind: on ? "" : k.kind, person: on ? "" : value.person })}
              className={cn(
                "inline-flex min-h-11 items-center gap-1 rounded-full border px-3 text-sm",
                on ? "border-[var(--gn-gold)] bg-amber-50 font-semibold" : "bg-surface hover:bg-muted",
              )}
            >
              <span aria-hidden>{k.icon}</span>
              {k.label}
            </button>
          );
        })}
      </div>

      {value.kind ? (
        <>
          <div className="space-y-1">
            <span className="text-xs font-medium">{isBirthday ? "Ngày sinh nhật" : "Ngày của dịp (nếu có)"}</span>
            <DayMonthInput
              label={isBirthday ? "Ngày sinh nhật" : "Ngày của dịp"}
              value={value.date}
              disabled={disabled}
              onChange={(v) => set({ date: v.iso, dateError: v.error })}
            />
            <p className="text-[11px] text-muted-foreground">Chỉ cần ngày và tháng — năm biết thì ghi, không thì bỏ trống.</p>
          </div>

          {isBirthday ? (
            <div className="space-y-1.5">
              <span className="text-xs font-medium">Sinh nhật của ai?</span>
              <div role="radiogroup" aria-label="Sinh nhật của ai" className="flex flex-wrap gap-1.5">
                {(
                  [
                    ["self", "Của người đặt — lưu vào hồ sơ khách"],
                    ["other", "Của người khác (bé, bố mẹ…)"],
                  ] as const
                ).map(([key, label]) => (
                  <button
                    key={key}
                    type="button"
                    role="radio"
                    aria-checked={value.person === key}
                    disabled={disabled}
                    onClick={() => set({ person: key })}
                    className={cn(
                      "min-h-11 rounded-md border px-3 text-left text-sm",
                      value.person === key ? "border-[var(--gn-gold)] bg-amber-50 font-semibold" : "bg-surface hover:bg-muted",
                    )}
                  >
                    {label}
                  </button>
                ))}
              </div>
              {value.person === "other" ? (
                <Input
                  aria-label="Tên người có sinh nhật"
                  placeholder="Tên (vd: bé Na, bố Hùng)"
                  maxLength={120}
                  value={value.personName}
                  disabled={disabled}
                  onChange={(e) => set({ personName: e.target.value })}
                />
              ) : null}
            </div>
          ) : null}

          <Input
            aria-label="Ghi chú dịp chụp"
            placeholder="Ghi chú ngắn (vd: cần ảnh trước tiệc)"
            maxLength={200}
            value={value.note}
            disabled={disabled}
            onChange={(e) => set({ note: e.target.value })}
          />
        </>
      ) : null}
    </div>
  );
}
